/**
 * Shared chrome for every popup/sheet in the app (`ToolLaunchDialog`,
 * `StageRunPanel`, `InteractiveSegPanel`, `LaunchSheet`, `NewProject`,
 * `CommandPalette`, `HelpOverlays`) — a blurred scrim, a soft-shadowed
 * rounded panel, and a title block, in the Apple-sheet style: translucent
 * backdrop, generous radius, one clear accent action. Deliberately scoped to
 * popups only — the ribbon/menu bar/panels keep `components/ui/primitives`'
 * sharper, denser look, which is right for a toolbar and wrong for a sheet.
 *
 * Each caller keeps its own `z-[N]` (stacking order varies by panel, e.g.
 * `StageRunPanel` sits above `ToolLaunchDialog`) and its own width/height —
 * this only supplies the look, never the sizing or the open/close logic.
 */
import * as React from "react";
import { X } from "lucide-react";

import { cn } from "@/lib/utils";

interface DialogOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: "center" | "top";
}

export function DialogOverlay({ className, align = "center", children, ...rest }: DialogOverlayProps) {
  return (
    <div
      className={cn(
        "ads-dialog-overlay fixed inset-0 flex justify-center bg-black/20 p-6 backdrop-blur-sm",
        align === "top" ? "items-start pt-[8vh]" : "items-center",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  );
}

export const DialogPanel = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cn(
        "ads-dialog-panel flex flex-col overflow-hidden rounded-3xl bg-white shadow-2xl ring-1 ring-black/[0.06] outline-none",
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  ),
);
DialogPanel.displayName = "DialogPanel";

export function DialogTitle({
  title,
  subtitle,
  onClose,
  className,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  onClose?: () => void;
  className?: string;
}) {
  return (
    <div className={cn("flex shrink-0 items-start justify-between gap-3 px-6 pb-4 pt-6", className)}>
      <div className="min-w-0">
        <h2 className="truncate text-[17px] font-semibold tracking-tight text-ads-text">{title}</h2>
        {subtitle ? <p className="mt-1 text-[13px] leading-snug text-ads-muted">{subtitle}</p> : null}
      </div>
      {onClose ? (
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-neutral-400 transition-colors hover:bg-black/[0.05] hover:text-neutral-600"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
    </div>
  );
}

export function DialogFooter({ className, children }: { className?: string; children: React.ReactNode }) {
  return (
    <div className={cn("flex shrink-0 items-center justify-end gap-2 border-t border-black/[0.06] bg-neutral-50/60 px-6 py-4", className)}>
      {children}
    </div>
  );
}

/** One clear pill-shaped accent action — pass to `Button`'s `className`. */
export const dialogPrimaryButtonClass = "rounded-full px-4 shadow-sm";
/** A quiet, text-weight secondary/cancel action next to it. */
export const dialogSecondaryButtonClass =
  "rounded-full border-transparent bg-transparent text-ads-muted shadow-none hover:bg-black/[0.05] hover:text-ads-text";
